/** @format */

import { useCallback, useEffect, useMemo, useState } from "react";
import { BRANDS, Paper, PaperAttachment } from "@question-bank/shared";
import { useBrand } from "../../hooks/useBrand";
import { formatSeason } from "../../utils/helpers";
import { listPapers, upsertPaper } from "../../api/papers";
import { PdfUpload } from "../../components/PdfUpload";

const formatSeries = (series: Paper["series"]): string =>
	[formatSeason(series.season), series.year].filter(Boolean).join(" ") || "-";

const formatSubject = (subject: Paper["subject"]): string => {
	if (!subject) {
		return "—";
	}
	return Array.isArray(subject) ? subject.join(", ") : subject;
};

const attachmentCount = (paper: Paper): number => paper.attachments?.length ?? 0;

export const AdminPapersPage = () => {
	const { brand } = useBrand();
	const shouldSupportsWelsh = (BRANDS[brand].supportedLanguages as readonly string[]).includes("cy");

	const [papers, setPapers] = useState<Paper[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [subjectFilter, setSubjectFilter] = useState("");
	const [editing, setEditing] = useState<Paper | null>(null);
	const [draftAttachments, setDraftAttachments] = useState<PaperAttachment[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);

	const load = useCallback(async () => {
		setIsLoading(true);
		setError(null);
		try {
			setPapers(await listPapers(brand));
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load papers");
		} finally {
			setIsLoading(false);
		}
	}, [brand]);

	useEffect(() => {
		void load();
	}, [load]);

	const subjects = useMemo(() => {
		const set = new Set<string>();
		for (const paper of papers) {
			const values = Array.isArray(paper.subject) ? paper.subject : paper.subject ? [paper.subject] : [];
			values.forEach((value) => set.add(value));
		}
		return Array.from(set).sort((a, b) => a.localeCompare(b));
	}, [papers]);

	const filteredPapers = useMemo(() => {
		if (!subjectFilter) {
			return papers;
		}
		return papers.filter((paper) => {
			const values = Array.isArray(paper.subject) ? paper.subject : paper.subject ? [paper.subject] : [];
			return values.some((value) => value.toLowerCase() === subjectFilter.toLowerCase());
		});
	}, [papers, subjectFilter]);

	const startEditing = (paper: Paper) => {
		setEditing(paper);
		setDraftAttachments(paper.attachments?.map((attachment) => ({ ...attachment })) ?? []);
		setSaveError(null);
	};

	const updateAttachment = (id: string, patch: Partial<PaperAttachment>) => {
		setDraftAttachments((prev) =>
			prev.map((attachment) => (attachment.id === id ? { ...attachment, ...patch } : attachment)),
		);
	};

	const addAttachment = () => {
		setDraftAttachments((prev) => [...prev, { id: crypto.randomUUID() }]);
	};

	const removeAttachment = (id: string) => {
		setDraftAttachments((prev) => prev.filter((attachment) => attachment.id !== id));
	};

	const handleSave = async () => {
		if (!editing) {
			return;
		}
		// Drop attachments that carry no PDF at all (nothing uploaded yet).
		const attachments = draftAttachments.filter((attachment) => attachment.en || attachment.cy);
		// Every attachment must have an English PDF, and a Welsh PDF too for
		// brands that support Welsh (e.g. WJEC).
		const isIncomplete = attachments.some((attachment) => !attachment.en || (shouldSupportsWelsh && !attachment.cy));
		if (isIncomplete) {
			setSaveError(
				shouldSupportsWelsh
					? "Each attachment needs both an English and a Welsh PDF."
					: "Each attachment needs an English PDF.",
			);
			return;
		}
		setIsSaving(true);
		setSaveError(null);
		try {
			await upsertPaper(brand, editing.id, {
				paperCode: editing.paperCode,
				series: editing.series,
				date: new Date(editing.date),
				subject: editing.subject,
				qualification: editing.qualification,
				tier: editing.tier,
				// Guaranteed by the `incomplete` check above: every remaining
				// attachment has an English PDF (and a Welsh one for WJEC).
				attachments: attachments as (PaperAttachment & { en: string })[],
			});
			setEditing(null);
			await load();
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : "Failed to save paper");
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className="flex flex-col gap-6">
			<div>
				<h2 className="text-2xl font-bold text-slate-900">Papers</h2>
				<p className="mt-1 text-sm text-slate-600">
					Manage papers and their PDF attachments (e.g. question paper, data booklet). Attachments are shared by every
					question in the paper.
				</p>
			</div>

			<div className="flex flex-wrap items-end gap-3">
				<label className="flex flex-col gap-1 text-sm font-semibold text-slate-700">
					Filter by subject
					<select
						value={subjectFilter}
						onChange={(event) => setSubjectFilter(event.target.value)}
						className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal"
					>
						<option value="">All subjects</option>
						{subjects.map((subject) => (
							<option key={subject} value={subject}>
								{subject}
							</option>
						))}
					</select>
				</label>
			</div>

			{isLoading && <p className="text-sm text-slate-600">Loading papers…</p>}
			{error && <p className="text-sm text-red-600">{error}</p>}

			{!isLoading && !error && (
				<div className="overflow-x-auto rounded-lg bg-white shadow-(--admin-shadow-raised)">
					<table className="min-w-full divide-y divide-slate-200 text-left text-sm">
						<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
							<tr>
								<th className="px-4 py-3">Paper code</th>
								<th className="px-4 py-3">Series</th>
								<th className="px-4 py-3">Subject</th>
								<th className="px-4 py-3">Level</th>
								<th className="px-4 py-3">Tier</th>
								<th className="px-4 py-3">Root questions</th>
								<th className="px-4 py-3">Attachments</th>
								<th className="px-4 py-3" />
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{filteredPapers.map((paper) => (
								<tr key={paper.id}>
									<td className="px-4 py-3 font-semibold text-slate-800">{paper.paperCode}</td>
									<td className="px-4 py-3 text-slate-700">{formatSeries(paper.series)}</td>
									<td className="px-4 py-3 text-slate-700">{formatSubject(paper.subject)}</td>
									<td className="px-4 py-3 text-slate-700">
										{paper.qualification.title ?? paper.qualification.code ?? "—"}
									</td>
									<td className="px-4 py-3 text-slate-700">
										{paper.tier ? (paper.tier.title ?? paper.tier.code) : "—"}
									</td>
									<td className="px-4 py-3 text-slate-700">{paper.rootQuestionCount ?? 0}</td>
									<td className="px-4 py-3 text-slate-700">{attachmentCount(paper)}</td>
									<td className="px-4 py-3 text-right">
										<button
											type="button"
											onClick={() => startEditing(paper)}
											className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-(--admin-color-primary-strong) hover:bg-slate-50"
										>
											Manage attachments
										</button>
									</td>
								</tr>
							))}
							{filteredPapers.length === 0 && (
								<tr>
									<td colSpan={8} className="px-4 py-6 text-center text-sm text-slate-500">
										No papers found.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			)}

			{editing && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
					<div className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-4 overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
						<div className="flex items-start justify-between">
							<div>
								<h3 className="text-lg font-bold text-slate-900">
									{editing.paperCode} · {formatSeries(editing.series)}
								</h3>
								<p className="text-sm text-slate-600">{formatSubject(editing.subject)}</p>
							</div>
							<button
								type="button"
								onClick={() => setEditing(null)}
								className="text-sm font-semibold text-slate-500 hover:text-slate-700"
							>
								Close
							</button>
						</div>

						<p className="text-sm text-slate-600">
							{shouldSupportsWelsh
								? "Each attachment requires both an English and a Welsh PDF."
								: "Each attachment requires an English PDF."}
						</p>

						<div className="flex flex-col gap-4">
							{draftAttachments.map((attachment) => (
								<div key={attachment.id} className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4">
									<div className="flex items-center gap-3">
										<label className="flex-1 text-sm font-semibold text-slate-700">
											Label
											<input
												type="text"
												value={attachment.label ?? ""}
												onChange={(event) => updateAttachment(attachment.id, { label: event.target.value })}
												placeholder="e.g. Question paper, Data booklet"
												className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal"
											/>
										</label>
										<button
											type="button"
											onClick={() => removeAttachment(attachment.id)}
											className="mt-6 text-sm font-semibold text-red-600 hover:text-red-700"
										>
											Remove
										</button>
									</div>
									<div className="grid gap-4 sm:grid-cols-2">
										<PdfUpload
											label="English PDF"
											brand={brand}
											assetKey={attachment.en}
											onUpload={(key) => updateAttachment(attachment.id, { en: key })}
											onRemove={() => updateAttachment(attachment.id, { en: undefined })}
										/>
										{shouldSupportsWelsh && (
											<PdfUpload
												label="Welsh PDF"
												brand={brand}
												assetKey={attachment.cy}
												onUpload={(key) => updateAttachment(attachment.id, { cy: key })}
												onRemove={() => updateAttachment(attachment.id, { cy: undefined })}
											/>
										)}
									</div>
								</div>
							))}

							<button
								type="button"
								onClick={addAttachment}
								className="self-start rounded-lg border border-dashed border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
							>
								+ Add attachment
							</button>
						</div>

						{saveError && <p className="text-sm text-red-600">{saveError}</p>}

						<div className="flex justify-end gap-3">
							<button
								type="button"
								onClick={() => setEditing(null)}
								className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={() => void handleSave()}
								disabled={isSaving}
								className="rounded-lg bg-(--admin-color-primary-strong) px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
							>
								{isSaving ? "Saving…" : "Save"}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
